'use client'

import Link from 'next/link'
import { useEffect, useState } from 'react'

import { Container } from '@/components/marketing/container'
import { ThemeToggle } from '@/components/theme/theme-toggle'
import { Button } from '@/components/ui/button'
import { SITE } from '@/lib/marketing/site'
import { cn } from '@/lib/utils'
import { useTranslations } from 'next-intl'

/**
 * Public chrome: wordmark, theme switch, "Log in", and — on the landing page
 * only — a "Join the beta" button that arrives once the hero's own form has
 * scrolled away.
 *
 * Shared by the landing page and the legal documents, so `/terms` and `/privacy`
 * always have a way back to the top of the funnel rather than stranding a reader
 * on a dead-end page. `joinHref` is what distinguishes the two uses: the legal
 * pages pass nothing, because a button that scrolls to a form which is not on the
 * page is a broken control.
 *
 * ## The dark-mode contrast trap
 *
 * A `.navactions a` rule (specificity 0,1,1) outranked `.btn` (0,1,0) in the
 * mockup and the header button rendered muted grey on the accent fill —
 * unreadable in dark. Nothing here reproduces it, because the button's colour
 * arrives as Tailwind utilities on the element itself and this file defines **no
 * descendant link-colour rule** for it to lose to. Keep it that way: styling the
 * links in this bar with a `nav a { … }`-shaped rule would reintroduce exactly
 * that bug, and it is invisible until someone loads the page in dark mode.
 */
export function MarketingHeader({ joinHref }: { joinHref?: string }) {
  const t = useTranslations()

  // One threshold now. The chrome settles at the first hint of scrolling.
  //
  // The CTA used to have a second one (`pastHero`), hiding itself until the
  // hero's form had scrolled away so that it "never competes with the form it
  // duplicates". It cost more than it bought: hidden-not-unmounted, it still
  // reserved 108px + a 12px gap, leaving 144px of dead space beside "Log in" at
  // scroll-top, and it pointed at the hero input — so clicking it from anywhere
  // below scrolled UP, past the threshold, and the button that had just been
  // clicked disappeared. Decision, Xavier, 2026-08-15: always visible, pointing
  // at the closing form. It is the page's primary action.
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  return (
    <header
      className={cn(
        // The bottom rule is there at EVERY scroll position, not only once the
        // bar has settled. Transparent-until-scrolled left the wordmark and the
        // two controls floating over the hero with nothing under them, which
        // read as a rendering fault rather than as restraint. What scrolling
        // still changes is the ground: the bar goes translucent-and-blurred once
        // there is content to sit over.
        'sticky top-0 z-50 w-full border-b border-border transition-colors duration-200',
        scrolled &&
          // `supports-[backdrop-filter]` so a browser without backdrop blur gets
          // an opaque bar rather than translucent text over content.
          'bg-background/85 supports-[backdrop-filter]:backdrop-blur-sm',
      )}
    >
      <Container className="flex items-center justify-between gap-4 py-4">
        <Link
          href="/"
          className="font-serif text-lg font-semibold tracking-tight text-foreground"
        >
          {SITE.name}
        </Link>
        <div className="flex items-center gap-2 sm:gap-3">
          <ThemeToggle />
          <Link
            href="/sign-in"
            // `text-foreground`, not `text-muted-foreground`: this is a primary
            // navigation action, and muted measures 3.68:1 against the paper
            // background — below AA for 14px text.
            className="text-sm text-foreground underline-offset-4 transition-colors hover:underline"
          >
            {t('marketing.nav.logIn')}
          </Link>
          {joinHref ? (
            <Button
              asChild
              size="sm"
              className="bk-hover-raise rounded-control"
            >
              <Link href={joinHref}>{t('marketing.nav.joinBeta')}</Link>
            </Button>
          ) : null}
        </div>
      </Container>
    </header>
  )
}
